import { useId, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import {
  PRODUCT_APPROVAL_HISTORY_PATH,
  PRODUCT_URL,
} from "@/common/constants";
import { ClipSpinner } from "@/common/components/spinner";
import { IProductApprovalHistory } from "@/common/types";
import useQuery from "@/hooks/useQuery";
import { EditHistoryWrapper } from "./style";
import { EditEntry } from "./subs/edit-entry";

/** How many edits are fetched at a time, and how many "Show earlier edits" adds. */
const PAGE_SIZE = 25;

export interface IEditHistoryProps {
  productId?: string;
}

/**
 * MED-682 (UC-PROD-005) — the product's edit history, collapsed inside the
 * product detail modal.
 *
 * Read-only: a manufacturer can see that a product was changed, when, by whom
 * and from what to what. There is no revert and no approval action here — the
 * backend exposes no write endpoint for history, and a past edit is a record,
 * not something to act on.
 *
 * Reads `GET /products/:id/approval-history?event_types=resubmitted&order=desc`
 * — the same endpoint and the same ownership check as the admin panel, narrowed
 * to edits and reversed. Scoping is entirely the backend's: it serves this
 * product only, and only to an admin or the owning company.
 */
export function EditHistory({ productId }: IEditHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  // ProductDetails renders in three places, so a hardcoded id could appear
  // twice in one document. useId is unique per component instance. It is put on
  // aria-controls only while the panel is open, because the attribute must not
  // point at an element that is not in the document.
  const panelId = useId();

  const url =
    PRODUCT_URL +
    "/" +
    productId +
    PRODUCT_APPROVAL_HISTORY_PATH +
    `?event_types=resubmitted&order=desc&page=1&per_page=${perPage}`;

  // Fetched on expand rather than on mount: the panel is collapsed every time
  // the modal opens, so fetching eagerly would cost a request per product view
  // for a panel most views never open. Re-expanding refetches, which is what
  // keeps the list current after the user edits the product from this modal.
  //
  // Partial<>: useQuery seeds `data` with [] until the first response lands.
  const { data, error, loading, meta, refetch } = useQuery<
    Partial<IProductApprovalHistory>
  >(url, expanded && Boolean(productId));

  const events = data?.events ?? [];
  const totalCount = meta?.total_count;
  // `total_count` is the filtered count — edits, not every lifecycle event —
  // so it is safe to caption the panel with.
  const hasMore = typeof totalCount === "number" && events.length < totalCount;

  // `meta` is populated only by a successful response, so this — not `loading` —
  // is what separates "there are no edits" from "the first request has not
  // landed". useQuery reports `loading: false` until its effect runs one render
  // after the expand, so keying either state off `loading` alone flashes an
  // empty panel on the way in.
  const hasLoaded = typeof totalCount === "number";
  const showSpinner = !hasLoaded && !error;
  const showEmpty = hasLoaded && !loading && events.length === 0;

  return (
    <EditHistoryWrapper>
      <button
        type="button"
        className="toggle"
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        onClick={() => setExpanded((open) => !open)}
      >
        <span className="toggle-label">
          Edit History
          {typeof totalCount === "number" ? (
            <span className="toggle-count">{totalCount}</span>
          ) : null}
        </span>
        <MdKeyboardArrowDown
          className={expanded ? "chevron open" : "chevron"}
          aria-hidden="true"
        />
      </button>

      {expanded ? (
        <div className="panel" id={panelId} role="region" aria-label="Edit history">
          {showSpinner ? (
            <div className="panel-state">
              <ClipSpinner />
              <span className="state-text">Loading edit history...</span>
            </div>
          ) : null}

          {error ? (
            <div className="panel-state">
              <span className="state-text">
                Could not load the edit history.
              </span>
              <button
                type="button"
                className="retry"
                onClick={() => refetch()}
              >
                Try again
              </button>
            </div>
          ) : null}

          {showEmpty ? (
            <div className="panel-state">
              <span className="state-text">
                This product has not been edited.
              </span>
              <span className="state-note">
                Edits are recorded from the point history tracking began, so
                anything changed before then is not listed here.
              </span>
            </div>
          ) : null}

          {events.length > 0 ? (
            <ul className="entries">
              {events.map((event) => (
                <EditEntry key={event.id} event={event} />
              ))}
            </ul>
          ) : null}

          {hasMore ? (
            <button
              type="button"
              className="show-more"
              onClick={() => setPerPage((size) => size + PAGE_SIZE)}
            >
              Show earlier edits ({events.length} of {totalCount} shown)
            </button>
          ) : null}
        </div>
      ) : null}
    </EditHistoryWrapper>
  );
}
